import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Breadcrumbs, Button, FormWrapper, SubmitButton, Table } from "../components";
import { useCoupon } from "../hooks";

function CouponCodePage() {
  const initialFormData = {
    number_of_uses: "",
    discount: "",
    agents: [],
    hours: "",
    minutes: "",
    all_agents: false,
  };
  const [formData, setFormData] = useState(initialFormData);
  const [agentsInput, setAgentsInput] = useState("");
  const [generatedCoupon, setGeneratedCoupon] = useState({
    id: "",
    coupon_code: "",
  });
  const { createNewCoupon, regenerateCoupon, fetchAllCoupons } = useCoupon();
  const [copied, setCopied] = useState(false);

  const handleAgentsInput = (e) => {
    const inputValue = e.target.value;
    setAgentsInput(inputValue);

    // Split the input value by comma or space
    // The input can be '123, 456, 789' or '123 456 789' or '123,456,789' or '123, 456 789'
    const agents = inputValue.split(/[ ,]+/);
    setFormData({ ...formData, agents: agents });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAllAgentsToggle = () => {
    setFormData((prev) => ({ ...prev, all_agents: !prev.all_agents }));
  };

  const validateFormData = () => {
    if (!formData.number_of_uses || !formData.discount || !formData.agents || !formData.hours || !formData.minutes)
      return false;

    if (formData.number_of_uses < 1 || isNaN(formData.number_of_uses)) {
      toast.error("Number of uses should be a positive integer.");
      return false;
    }

    if (formData.discount < 0 || isNaN(formData.discount)) {
      toast.error("Discount should be a positive number.");
      return false;
    }

    if (!formData.all_agents && formData.agents.length < 1) {
      toast.error("Please enter at least one agent ID.");
      return false;
    }

    if (formData.agents.some((agent) => isNaN(agent))) {
      toast.error("Agent ID should be a number.");
      return false;
    }

    return true;
  };

  const handleCreateCoupon = async (e) => {
    e.preventDefault();

    const isValid = validateFormData();
    if (!isValid) return;

    const data = {
      number_of_uses: Number(formData.number_of_uses),
      discount: Number(formData.discount),
      agents: formData.all_agents ? [] : formData.agents.map((agent) => Number(agent)),
      expiry_time: `${formData.hours}:${formData.minutes}`,
      all_agents: formData.all_agents,
    };

    const { coupon, error } = await createNewCoupon(data);
    if (error) return toast.error(error);
    toast.success("Coupon created successfully");
    setGeneratedCoupon({ id: coupon.id, coupon_code: coupon.code });
    setCopied(false);
    setFormData(initialFormData);
    setAgentsInput("");
    await fetchPastCoupons();
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(generatedCoupon.coupon_code);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 10000);
  };

  const handleRegenerate = async () => {
    const { coupon, error } = await regenerateCoupon(generatedCoupon.id);
    if (error) return toast.error(error);
    setGeneratedCoupon((prev) => ({ ...prev, coupon_code: coupon.code }));
    setCopied(false);
  };

  const couponHistoryTableColumns = [
    { id: "date", name: "Date" },
    { id: "agents", name: "Coupon created for" },
    { id: "discount", name: "Discount Amount (₹)" },
    { id: "number_of_uses", name: "No. of uses" },
    { id: "time_left", name: "Time left" },
  ];

  const [pastCoupons, setPastCoupons] = useState([]);
  const fetchPastCoupons = async () => {
    const { coupons, error } = await fetchAllCoupons();
    if (error || !coupons) return;
    setPastCoupons(coupons);
  };

  useEffect(() => {
    const timer = setInterval(() => {
      const newPastCoupons = pastCoupons.map((coupon) => {
        const { expires_at } = coupon.expiry;
        const diff = new Date(expires_at) - new Date();
        const hours = `${Math.floor((diff / (1000 * 60 * 60)) % 24)}`.padStart(2, "0");
        const minutes = `${Math.floor((diff / (1000 * 60)) % 60)}`.padStart(2, "0");
        const seconds = `${Math.floor((diff / 1000) % 60)}`.padStart(2, "0");
        const timeLeft = `${hours}:${minutes}:${seconds}`;

        if (diff <= 0) {
          return { ...coupon, time_left: "Expired" };
        }

        return { ...coupon, time_left: timeLeft };
      });
      setPastCoupons(newPastCoupons);
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [pastCoupons]);

  useEffect(() => {
    fetchPastCoupons();
  }, []);

  return (
    <>
      <Breadcrumbs breadcrumbs={[{ name: "Coupon Code" }]} />
      <div className="section_wrapper">
        <div className="section_title_wrapper">
          <h2 className="section_title">Coupon Code </h2>
        </div>
        <FormWrapper id="new-coupon-form" onSubmit={handleCreateCoupon} className="form-w-75" title="Create a coupon">
          <input
            type="number"
            name="number_of_uses"
            min="1"
            className="input"
            onChange={handleInputChange}
            value={formData.number_of_uses}
            placeholder="Enter number of uses"
            required
          />
          <div className="input_with_prefix_wrapper full">
            <span className="prefix">₹</span>
            <input
              type="number"
              name="discount"
              className="input"
              placeholder="Enter discount amount"
              onChange={handleInputChange}
              value={formData.discount}
              min={1}
              required
            />
          </div>
          <div className="row">
            <input
              type="text"
              name="agents"
              className="input full"
              placeholder="Enter Agent ID (Enter one or multiple agents ID)"
              onChange={handleAgentsInput}
              value={agentsInput}
              required={!formData.all_agents}
              disabled={formData.all_agents}
            />
            <span className="gray small">or</span>
            <label htmlFor="all_agents" className="row gray">
              <input
                type="checkbox"
                name="all_agents"
                id="all_agents"
                checked={formData.all_agents}
                onChange={handleAllAgentsToggle}
                className="input"
              />
              All Agents
            </label>
          </div>
          <div className="row start">
            <label htmlFor="time" className="label">
              Set expiry hours
            </label>
            <div className="row">
              <input
                type="number"
                name="hours"
                onChange={handleInputChange}
                value={formData.hours}
                className="input time"
                placeholder="Hours"
                min={0}
                max={23}
                required
              />
              <span>:</span>
              <input
                type="number"
                name="minutes"
                className="input time"
                placeholder="Minutes"
                min={0}
                max={59}
                required
                onChange={handleInputChange}
                value={formData.minutes}
              />
            </div>
          </div>
          <SubmitButton form="new-coupon-form" start>
            Create Coupon
          </SubmitButton>
        </FormWrapper>
        <div className="coupon_code_wrapper">
          <div className="coupon_code">{generatedCoupon.coupon_code}</div>
          <Button
            className="ghost"
            icon="solar:copy-linear"
            iconPosition="end"
            onClick={handleCopyToClipboard}
            disabled={!generatedCoupon.id}
          >
            {copied ? "Copied!" : "Copy code"}
          </Button>
          <Button
            className="ghost"
            icon="solar:restart-linear"
            iconPosition="end"
            onClick={handleRegenerate}
            disabled={!generatedCoupon.id}
          >
            Generate New
          </Button>
        </div>
      </div>

      <div className="section_wrapper">
        <div className="section_title_wrapper">
          <h2 className="section_title">Coupon History</h2>
        </div>
        <Table columns={couponHistoryTableColumns} data={pastCoupons} />
      </div>
    </>
  );
}

export default CouponCodePage;
